import { useMemo, useCallback } from 'react';
import 'react-contexify/dist/ReactContexify.css';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { Box } from '@mui/system';
import { Divider, Typography } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { ApplicationStageDivergence } from 'src/utils/applications-kanban';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { JobVerificationStatus } from 'src/services/jobs/jobs.types';
import { openPlacementEditor } from 'src/store/slices/jobs/jobsSlice';
import { isShadowEmail } from 'src/services/candidates/candidates.utils';
import { useInviteApplicationCandidateMutation } from 'src/services/jobs/jobs.service';
import { openInterviewSchedulerModal } from 'src/store/slices/interviews/interviewSlice';
import { openSingleMailerDialog, openTaskEditorSidesheet } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useGetReferenceRequestsByApplicationQuery } from 'src/services/references/references.service';
import { setShowOfferModal, setShowActionStageRequestModal } from 'src/store/slices/applications/applicationsSlice';
import { isApplicationFinalized, getInviteToRepresentErrorMessage } from 'src/services/applications/applications.utils';
import { ApplicationStage, RepresentationStatus, StageChangeRequestStatus } from 'src/services/applications/applications.types';
import { useGetApplicationByIdQuery, useGetApplicationOffersQuery, useGetApplicationStageChangeRequestsQuery, useGetOutgoingApplicationResourceRequestsQuery } from 'src/services/applications/applications.service';

import { NestedMenu } from 'src/components/menu/nested-menu';

import { TenantType } from 'src/types/enums';

type Props = {
  anchorEl: HTMLElement | null;
  applicationId: string;
  showInfo?: boolean;
  closeMenu?: () => void;
  onOpenResourceRequestsModal: () => void;
  onOpenOutgoingResourceRequestsModal: () => void;
  onOpenReferenceRequestListModal: () => void;
  onOpenReferenceInitiateModal: () => void;
  onOpenInterviewListModal: () => void;
  onOpenStageChangeConfirm: (stage: ApplicationStage) => void;
  onOpenRequestStageChangeConfirm: (stage: ApplicationStage) => void;
  onOpenOfferListModal: () => void;
  onOpenStageChangeRequestModal?: () => void;
  onOpenInterviewSchedulerModal?: () => void;
  onOpenExtendOfferModal?: () => void;
  onOpenInterviewPhaseModal?: () => void;
  onQuickViewApplication?: () => void;
};

export default function ApplicationActionMenu({
  anchorEl,
  applicationId,
  showInfo = false,
  closeMenu = () => { },
  onOpenResourceRequestsModal,
  onOpenOutgoingResourceRequestsModal,
  onOpenReferenceRequestListModal,
  onOpenReferenceInitiateModal,
  onOpenInterviewListModal,
  onOpenStageChangeConfirm,
  onOpenRequestStageChangeConfirm,
  onOpenOfferListModal,
  onOpenStageChangeRequestModal = () => { },
  onOpenInterviewSchedulerModal = () => { },
  onOpenExtendOfferModal = () => { },
  onOpenInterviewPhaseModal = () => { },
  onQuickViewApplication = () => { },
}: Props) {
  const tenantType = useOrgTenant();
  const { t } = useTranslate();
  const dispatch = useAppDispatch();

  const [inviteToRepresent] = useInviteApplicationCandidateMutation();

  const { currentData: pendingReferenceRequests } = useGetReferenceRequestsByApplicationQuery(
    {
      applicationId: applicationId as string,
      params: { page: 1, per_page: 5 },
      status: 'pending'
    },
    { skip: !applicationId }
  );

  const { currentData: application, isFetching: isLoadingApplication } = useGetApplicationByIdQuery(applicationId, {
    skip: !applicationId,
  });

  const { currentData: resourceRequests } = useGetOutgoingApplicationResourceRequestsQuery(
    {
      applicationId: applicationId as string,
      params: { page: 1, per_page: 5 },
    },
    { skip: !applicationId }
  );

  const { currentData: offers, isFetching: isLoadingOffers } = useGetApplicationOffersQuery(
    {
      applicationId: applicationId as string,
      params: { page: 1, per_page: 1 },
    },
    { skip: !applicationId }
  );

  const { currentData: stageChangeRequests, isFetching: isLoadingRequests } = useGetApplicationStageChangeRequestsQuery(
    {
      applicationId: applicationId as string,
      params: { page: 1, per_page: 1, status: StageChangeRequestStatus.PENDING },
    },
    { skip: !applicationId }
  );

  const onExtendOffer = useCallback(() => {
    if (!application) return;
    dispatch(setShowOfferModal({ applicationId: applicationId as string }));
    onOpenExtendOfferModal();
  }, [applicationId, dispatch, application, onOpenExtendOfferModal]);

  const onOpenStageChangeRequestListModal = useCallback(() => {
    if (!application) return;
    dispatch(setShowActionStageRequestModal(stageChangeRequests?.results[0].id as string));
    onOpenStageChangeRequestModal();
  }, [dispatch, stageChangeRequests?.results, application, onOpenStageChangeRequestModal]);

  const onOpenInterviewModal = useCallback(() => {
    if (!application) return;
    dispatch(openInterviewSchedulerModal({ applicationId: applicationId as string }));
    onOpenInterviewSchedulerModal();
  }, [applicationId, dispatch, application, onOpenInterviewSchedulerModal]);

  const handleSendMail = useCallback(() => {
    if (!application) return;

    const candidateEmail = application.attached_profile?.email;

    if (!candidateEmail) {
      return;
    }

    dispatch(openSingleMailerDialog({
      emails: [candidateEmail],
      templateType: EmailMessageTemplateType.APPLICATION_UPDATE,
      templateEntityId: application.id,
      targetType: MailerTargetType.APPLICANT,
      targetId: application.id
    }));

  }, [application, dispatch]);

  const handleInviteToRepresent = useCallback(async () => {
    if (!application) return;

    const toastId = enqueueSnackbar(t('applications.api.invite_to_represent.loading'), {
      variant: 'loading',
      persist: true
    });

    try {

      await inviteToRepresent({
        application_id: applicationId,
      }).unwrap();

      closeSnackbar(toastId);

      enqueueSnackbar(t('applications.api.invite_to_represent.success'), {
        variant: 'success',
      });

    }
    catch (error) {
      const errMsg = getInviteToRepresentErrorMessage(error);
      closeSnackbar(toastId);
      enqueueSnackbar(errMsg, { variant: 'error' });
    }

  }, [applicationId, application, inviteToRepresent, t]);

  const canScheduleInterview = useMemo(() => tenantType !== TenantType.Candidate, [tenantType]);

  const handleStageChange = useCallback(
    (stage: ApplicationStage) => {
      if (!application) return;
      if (tenantType === TenantType.Recruiter) {
        onOpenStageChangeConfirm(stage);
      } else if (tenantType === TenantType.Client) {
        if (application.manager_type === TenantType.Client) {
          onOpenStageChangeConfirm(stage);
        } else {
          onOpenRequestStageChangeConfirm(stage);
        }
      }
    },
    [tenantType, application, onOpenStageChangeConfirm, onOpenRequestStageChangeConfirm]
  );

  const offerDisabledReason = useMemo((): string | null => {
    if (!application) return null;

    if (isApplicationFinalized(application.stage as ApplicationStage)) {
      return t('applications.detail.actions.offer_application_finalized');
    }

    if (tenantType === TenantType.Recruiter && application.job.verification_status === JobVerificationStatus.VERIFIED) {
      return t('applications.detail.actions.offer_triparty_application_tooltip');
    }

    return null;
  }, [application, tenantType, t]);

  const handleOpenPlacementCreator = useCallback(() => {
    if (!application) return;
    dispatch(openPlacementEditor({
      jobId: application.job.id,
      applicationId: application.id,
    }))

  }, [application, dispatch]);


  const handleAddTask = useCallback(() => {
    dispatch(openTaskEditorSidesheet({
      applicationId: application?.id || null,
    }));
  }, [dispatch, application]);

  const candidateHasEmail = !isShadowEmail(application?.attached_profile?.email);

  const menuItems = useMemo(() => {

    const items = [
      ...onQuickViewApplication ? [{
        label: t('applications.detail.actions.quick_view'),
        onClick: onQuickViewApplication
      }] : [],
      ...(isApplicationFinalized(application?.stage as ApplicationStage)
        ? []
        : [
          {
            label: t('applications.detail.actions.stage_actions'),
            children: [
              ...(ApplicationStageDivergence.includes(application?.stage as ApplicationStage)
                ? [
                  {
                    label: t('applications.detail.actions.shortlist'),
                    onClick: () => handleStageChange(ApplicationStage.SHORTLISTED),
                    disabled: isLoadingApplication,
                  },
                ]
                : []),
              {
                label: t('applications.detail.actions.reject'),
                onClick: () => handleStageChange(ApplicationStage.TO_BE_REJECTED),
                disabled:
                  isLoadingApplication ||
                  !application ||
                  [
                    ApplicationStage.REJECTED,
                    ApplicationStage.WITHDRAWN,
                    ApplicationStage.TO_BE_REJECTED,
                  ].includes(application.stage),
              },
            ],
          },
        ]),

      ...(application?.representation_status === RepresentationStatus.ACTIVE) ?
        [{
          label: t('applications.detail.actions.resource_requests'),
          badge: {
            content: resourceRequests?.count || 0,
            color: 'error',
          },
          children: [
            ...(tenantType !== TenantType.Candidate
              ? [
                {
                  label: t('applications.detail.actions.request_information'),
                  onClick: () => onOpenResourceRequestsModal(),
                  disabled: isLoadingApplication || isApplicationFinalized(application?.stage as ApplicationStage),
                },
              ]
              : []),
            {
              label: t('applications.detail.actions.view_resource_requests'),
              onClick: () => onOpenOutgoingResourceRequestsModal()
            },
          ],
        }] : [],

      ...(application?.representation_status === RepresentationStatus.ACTIVE) ?
        [{
          label: t('applications.detail.actions.references'),
          badge: {
            content: pendingReferenceRequests?.count || 0,
            color: 'error',
          },
          children: [
            ...(tenantType !== TenantType.Candidate
              ? [
                {
                  label: t('applications.detail.actions.request_references'),
                  onClick: () => onOpenReferenceInitiateModal(),
                  disabled: isLoadingApplication || isApplicationFinalized(application?.stage as ApplicationStage),
                },
              ]
              : []),
            {
              label: t('applications.detail.actions.view_reference_requests'),
              onClick: () => onOpenReferenceRequestListModal(),
            },
          ],
        }] : [],
      ...(application?.representation_status === RepresentationStatus.ACTIVE) ?
        [{
          label: t('applications.detail.actions.interviews'),
          children: [
            {
              label: t('applications.detail.actions.schedule_interview'),
              onClick: onOpenInterviewModal,
              disabled: isLoadingApplication || !canScheduleInterview || isApplicationFinalized(application?.stage as ApplicationStage),
            },
            {
              label: t('applications.detail.actions.view_interviews'),
              onClick: () => onOpenInterviewListModal(),
              disabled: isLoadingApplication,
            },
            ...(application?.stage === ApplicationStage.INTERVIEW) ? [{
              label: t('applications.detail.actions.update_interview_phase'),
              onClick: onOpenInterviewPhaseModal,
            }] : []
          ],
        }] : [],
      ...(tenantType !== TenantType.Candidate) ? [
        {
          label: t('applications.detail.actions.placements'),
          children: [
            {
              label: t('applications.detail.actions.create_placement'),
              onClick: handleOpenPlacementCreator,
              disabled: isLoadingApplication || isApplicationFinalized(application?.stage as ApplicationStage),
            },
          ],
        },
      ] : [],
      {
        label: t('applications.detail.actions.offers'),
        badge: {
          content: offers?.count || 0,
          color: 'error',
        },
        children: [
          ...(tenantType !== TenantType.Candidate
            ? [
              {
                label: t('applications.detail.actions.offer'),
                onClick: onExtendOffer,
                disabled:
                  (tenantType === TenantType.Recruiter &&
                    application?.job.verification_status === JobVerificationStatus.VERIFIED) ||
                  isApplicationFinalized(application?.stage as ApplicationStage),
                tooltip: offerDisabledReason ? {
                  title: offerDisabledReason,
                  placement: 'left',
                } : undefined,
              },
            ]
            : []),
          {
            label: t('applications.detail.actions.view_offers'),
            onClick: () => onOpenOfferListModal(),
            disabled: isLoadingOffers,
          }
        ],
      },
      ...(tenantType === TenantType.Recruiter
        ? [
          {
            label: t('applications.detail.actions.manage_requests'),
            badge: {
              content: stageChangeRequests?.count || 0,
              color: 'error',
            },
            onClick: onOpenStageChangeRequestListModal,
            disabled: !stageChangeRequests?.count || isLoadingRequests,
          },
        ]
        : []),

      ...(application?.representation_status === RepresentationStatus.INACTIVE && candidateHasEmail) ?
        [{
          label: t('applications.detail.actions.invite_to_represent'),
          onClick: handleInviteToRepresent
        }] : [],

      ...(candidateHasEmail ? [{
        label: t('applications.detail.actions.send_mail'),
        onClick: handleSendMail
      }] : []),
      {
        label: t('common.add_task'),
        onClick: handleAddTask
      }
    ];

    return isLoadingApplication ? [{ label: t('common.loading'), disabled: true }] : items;
  }, [
    t,
    resourceRequests?.count,
    pendingReferenceRequests?.count,
    offers?.count,
    stageChangeRequests?.count,
    isLoadingApplication,
    isLoadingOffers,
    isLoadingRequests,
    tenantType,
    application,
    canScheduleInterview,
    onOpenOutgoingResourceRequestsModal,
    onOpenResourceRequestsModal,
    onOpenReferenceRequestListModal,
    onOpenReferenceInitiateModal,
    onOpenOfferListModal,
    onExtendOffer,
    onOpenStageChangeRequestListModal,
    onOpenInterviewModal,
    onOpenInterviewListModal,
    handleStageChange,
    handleSendMail,
    handleInviteToRepresent,
    candidateHasEmail,
    offerDisabledReason,
    handleOpenPlacementCreator,
    onOpenInterviewPhaseModal,
    handleAddTask,
    onQuickViewApplication,
  ]);

  return (
    <Box>
      {showInfo && application && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {application?.attached_profile?.first_name} {application?.attached_profile?.last_name}
          </Typography>
          <Divider sx={{ my: 1 }} />
        </Box>
      )}
      <NestedMenu
        onClose={closeMenu}
        anchorEl={anchorEl}
        isLoading={isLoadingApplication || isLoadingOffers || isLoadingRequests}
        menuItems={menuItems}
      />
    </Box>
  );
}